import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AddUpdateKeyValuePairViewModel } from "../../types/viewModels";
import type { GetDataViewModel } from "../../types/viewModels/dataViews";
import type {
  KeyValuePairDTO,
  KeyValuePairWithGroupDTO,
} from "@repo/types/dtos";
import type { KeyValuePairType } from "@repo/types/enums";
import { useApiAccess } from "@repo/hooks/contexts";
import type { ExecutionResponse } from "@repo/types/responses";
import { keyValuePairKeys } from "@repo/types/queries";

export default function useAddUpdateKeyValuePair(enumType: number | undefined) {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      keyValuePair: AddUpdateKeyValuePairViewModel,
    ): Promise<ExecutionResponse> => {
      const res = await apiClient.post<ExecutionResponse>(
        "/keyValuePair/addUpdate",
        keyValuePair,
      );

      return res.data;
    },
    onSuccess: () => {
      // Invalidate all key value pair queries to ensure data consistency
      keyValuePairKeys.invalidateListByRelationId(queryClient, enumType);
    },
  });
}

export function useAddUpdateKeyValuePairs(enumType: number | undefined) {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      keyValuePairs: AddUpdateKeyValuePairViewModel[],
    ): Promise<ExecutionResponse> => {
      const res = await apiClient.post<ExecutionResponse>(
        "/keyValuePair/addUpdateMultiple",
        keyValuePairs,
      );

      return res.data;
    },
    onSuccess: () => {
      // Invalidate all key value pair queries to ensure data consistency
      keyValuePairKeys.invalidateListByRelationId(queryClient, enumType);
    },
  });
}

export function useGetKeyValuePair(
  keyValuePairType: KeyValuePairType,
  enumValue: number,
) {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: keyValuePairKeys.listByRelationId({
      keyValuePairType,
      enumValue,
    }),
    queryFn: async () => {
      const response = await apiClient.get<KeyValuePairDTO>(
        `/keyValuePair/getPair?keyValuePairType=${keyValuePairType}&enumValue=${enumValue}`,
      );
      return response.data;
    },
  });
}

export function useGetKeyValuePairs<T = KeyValuePairDTO>(
  enumType: number,
  select?: (data: KeyValuePairDTO[]) => T[],
) {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: keyValuePairKeys.listByRelationId(enumType),
    queryFn: async () => {
      const response = await apiClient.get<KeyValuePairDTO[]>(
        `/keyValuePair/get?enumType=${enumType}`,
      );
      return response.data;
    },
    select,
    enabled: !!enumType,
  });
}

export function useGetKeyValuePairGroups<T>(
  getDataViewModel: GetDataViewModel<T> | undefined,
) {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: keyValuePairKeys.listByRelationId(getDataViewModel),
    queryFn: async () => {
      const response = await apiClient.get<KeyValuePairWithGroupDTO<T>[]>(
        `/keyValuePair/getGroups`,
        {
          params: getDataViewModel,
        },
      );
      return response.data;
    },
    enabled:
      getDataViewModel != undefined &&
      getDataViewModel.ViewId !== null &&
      getDataViewModel.GroupBy !== undefined &&
      getDataViewModel.GroupBy !== null,
  });
}

export function useGetAllKeyValuePairs<T = KeyValuePairDTO>(
  select?: (data: KeyValuePairDTO[]) => T[],
) {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: keyValuePairKeys.list(),
    queryFn: async () => {
      const response =
        await apiClient.get<KeyValuePairDTO[]>(`/keyValuePair/get`);
      return response.data;
    },
    select,
  });
}
